import React, { useEffect, useState } from 'react';
import {
	Box,
	Button,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
} from '@mui/material';
import { type Repository } from '../types';
import { useGetRepositoryByIdQuery } from '../services/repositoryEntries';
import { useAppSelector } from '../hooks';
import { selectIsAuthenticated } from '../containers/authSlice';
import { selectAllPropertyDefinitions } from '../containers/contentSlice';
import TimelineCollectionsField, { isTimelineCollectionInputComplete, type TimelineCollectionInput } from './TimelineCollectionsField';

export interface AddTimelineDialogProps {
	open: boolean;
	repositories: Repository[];
	onClose: () => void;
	elementSelectSlot?: React.ReactNode;
	onSubmit: (payload: { name: string; collections: TimelineCollectionInput[] }) => void;
}

const AddTimelineDialog: React.FC<AddTimelineDialogProps> = ({
	open,
	repositories,
	onClose,
	onSubmit,
	elementSelectSlot,
}) => {
	const [collections, setCollections] = useState<TimelineCollectionInput[]>([]);
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const allPropertyDefinitions = useAppSelector(selectAllPropertyDefinitions);

	const firstCollection = collections[0];
	const { data: firstRepositoryById } = useGetRepositoryByIdQuery(firstCollection?.repositoryId ?? '', {
		skip: !open || !firstCollection?.repositoryId || !isAuthenticated,
	});

	useEffect(() => {
		if (!open) {
			return;
		}
		setCollections([{ repositoryId: '', datePropertyId: '' }]);
	}, [open]);

	const canSubmit = collections.length > 0 && collections.every(isTimelineCollectionInputComplete);

	const handleSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		if (!canSubmit) {
			return;
		}
		const firstPropertyDefinitions = isAuthenticated ? (firstRepositoryById?.propertyDefinitions ?? []) : allPropertyDefinitions;
		const repositoryTitle = repositories.find((r) => r.id === firstCollection.repositoryId)?.title;
		const propertyLabel = firstPropertyDefinitions.find((def) => def.id === firstCollection.datePropertyId)?.label;
		onSubmit({ name: `${repositoryTitle} by ${propertyLabel}`, collections });
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="body">
			<form onSubmit={handleSubmit} autoComplete="off">
				{elementSelectSlot != null ? (
					<Box display="flex" justifyContent="space-between" alignItems="center">
						<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>Add timeline</DialogTitle>
						<Box pr={2}>{elementSelectSlot}</Box>
					</Box>
				) : (
					<DialogTitle>Add timeline</DialogTitle>
				)}
				<Container>
					<Typography variant="body2" sx={{ color: 'text.secondary' }}>
						Timeline lays out entries from your collections left to right by day, using the date field you choose for each.
					</Typography>
				</Container>
				<DialogContent>
					<Box display="flex" flexDirection="column" gap={2} mt={elementSelectSlot != null ? 0 : 1}>
						<TimelineCollectionsField value={collections} repositories={repositories} onChange={setCollections} />
					</Box>
				</DialogContent>
				<DialogActions>
					<Button type="button" onClick={onClose}>
						Cancel
					</Button>
					<Button type="submit" variant="contained" disabled={!canSubmit}>
						Add
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};

export default AddTimelineDialog;
